import { useMutation, useQuery } from 'react-query';
import { deleteTechnicianNote, getNoteTypes, getTechnicianNotes, postTechnicianNote, putTechnicianNote } from 'queries';
import { parseErrors, Toast, useJobSearch } from 'helpers';
import { JsonDataItem } from 'types';

export const useTechnicianNotes = (page: number, per_page: number, technicianId?: string, type?: string) => {
  const {
    isLoading: isTechnicianNotesLoading,
    data: notes,
    refetch: refetchNotes,
  } = useQuery(['technician-notes', { technicianId, page, per_page, type }], getTechnicianNotes, {
    enabled: !!technicianId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { data: noteTypes } = useQuery(['note-types'], getNoteTypes, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: deleteNote, isLoading: isNoteRemoving } = useMutation(deleteTechnicianNote, {
    onSuccess: () => {
      Toast.show({ description: 'The note was successfully deleted.', type: Toast.Type.Success });
      refetchNotes();
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: createNote, isLoading: isNoteCreating } = useMutation(postTechnicianNote, {
    onSuccess: () => {
      Toast.show({ description: 'The note was successfully created.', type: Toast.Type.Success });
      refetchNotes();
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateNote, isLoading: isNoteUpdating } = useMutation(putTechnicianNote, {
    onSuccess: () => {
      Toast.show({ description: 'The note was successfully updated.', type: Toast.Type.Success });
      refetchNotes();
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const [jobs, _total = 0, isLinksLoading] = useJobSearch({
    searchBy: 'technician_id',
    searchValue: technicianId,
    page: 1,
    recordsPerPage: 10,
  });

  const linkOptions = [
    {
      label: 'Job',
      options: jobs?.map((job) => ({
        label: `#${job.ID} ${job.appointment}`,
        value: `J:${job.ID}`,
      })),
    },
    {
      label: 'Customer',
      options: jobs
        ?.filter((v, i, a) => a.findIndex((t) => t.customer_name === v.customer_name) === i)
        .map((job) => ({
          label: job.customer_name,
          value: `C:${job.customer_ID}`,
        })),
    },
  ];

  return {
    notes,
    noteTypes,
    deleteNote,
    createNote,
    updateNote,
    linkOptions,
    isLoading: isTechnicianNotesLoading || isNoteRemoving || isNoteUpdating || isNoteCreating || isLinksLoading,
  };
};
